import * as React from 'react'
import { useCardVerificationRequest } from './hooks'
import {
  CBadge,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDatePicker,
  CFormInput,
  CRow,
} from '@coreui/react-pro'
import { Alert, Divider, Image } from 'antd'
import { RejectModal } from '../atoms/rejectModal'
import { CardVerification } from './types'
import {
  setCardVerificationRequest,
  submitApprove,
  submitEditCardVerificationRequest,
} from './model'
import { path } from '../../../libs'
import { HistoriesWidget } from '../atoms/history'
import { CommentList } from '../../../views/widgets/commentList'
import { CommentForm } from '../../../views/widgets/commentForm'
import { CheckCircleOutlined, CloseSquareOutlined } from '@ant-design/icons'
import { Guard } from '../../../features/session/guard'

type Props = {
  className?: string
  cardVerificationRequest: CardVerification
}

export const CardVerificationRequestEdit = ({
  className,
  cardVerificationRequest,
}: Props) => {
  let {
    clientId,
    status,
    manager,
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    country,
    address,
    documentType,
    documentNumber,
    expirationDate,
    hasDeposit,
    browserFileNames,
  } = cardVerificationRequest

  const handleChange = (event: {
    currentTarget: { name: any; value: any }
  }) => {
    setCardVerificationRequest({
      ...cardVerificationRequest,
      [event.currentTarget.name]: event.currentTarget.value,
    })
  }

  return (
    <CCol className={className}>
      <Alert message="In development" type="error" />
      <CCard className="mb-4">
        <CCardBody>
          <CRow>
            <CCol>
              <CRow>
                <CCol xs={6} className="mb-2"></CCol>
                <CCol xs={6} className="mb-2"></CCol>
              </CRow>
              <Divider />
              <CRow>
                <CCol xs={4}>
                  <div className={'mb-1'}>
                    <CFormInput
                      placeholder="firstName"
                      autoComplete="firstName"
                      name="firstName"
                      value={firstName}
                      onChange={handleChange}
                      label={'First name'}
                    />
                  </div>
                </CCol>
                <CCol xs={4}>
                  <div className={'mb-1'}>
                    <CFormInput
                      placeholder="middleName"
                      autoComplete="middleName"
                      name="middleName"
                      value={middleName}
                      onChange={handleChange}
                      label={'Middle name'}
                    />
                  </div>
                </CCol>
                <CCol xs={4}>
                  <div className={'mb-1'}>
                    <CFormInput
                      placeholder="lastName"
                      autoComplete="lastName"
                      name="lastName"
                      value={lastName}
                      onChange={handleChange}
                      label={'Last name'}
                    />
                  </div>
                </CCol>
              </CRow>
              <Divider />
              <CRow>
                <CCol xs={4}>
                  <div>
                    <CDatePicker
                      date={dateOfBirth}
                      label="Date of birth"
                      locale="en-US"
                      onDateChange={(date, formatedDate) =>
                        handleChange({
                          currentTarget: {
                            name: 'dateOfBirth',
                            value: formatedDate,
                          },
                        })
                      }
                    />
                  </div>
                </CCol>
              </CRow>
              <Divider />
              <CRow>
                <CCol xs={8} className="mb-2">
                  <div className={'mb-1'}>
                    <CFormInput
                      placeholder="address"
                      autoComplete="address"
                      name="address"
                      value={address}
                      onChange={handleChange}
                      label={'Address'}
                    />
                  </div>
                </CCol>
                <CCol xs={4} className="mb-2">
                  <div className={'mb-1'}>
                    <CFormInput
                      placeholder="country"
                      autoComplete="country"
                      name="country"
                      value={country}
                      onChange={handleChange}
                      label={'Country'}
                    />
                  </div>
                </CCol>
              </CRow>
              <Divider />
              <CRow>
                <CCol xs={4} className="mb-2">
                  <div className={'mb-1'}>
                    <CFormInput
                      placeholder="documentType"
                      autoComplete="documentType"
                      name="documentType"
                      value={documentType}
                      onChange={handleChange}
                      label={'Document type'}
                    />
                  </div>
                </CCol>
                <CCol xs={4} className="mb-2">
                  <div className={'mb-1'}>
                    <CFormInput
                      placeholder="documentNumber"
                      autoComplete="documentNumber"
                      name="documentNumber"
                      value={documentNumber}
                      onChange={handleChange}
                      label={'Document number'}
                    />
                  </div>
                </CCol>
                <CCol xs={4} className="mb-2">
                  <div>
                    <CDatePicker
                      date={expirationDate}
                      label="Expiration date"
                      locale="en-US"
                      onDateChange={(date, formatedDate) =>
                        handleChange({
                          currentTarget: {
                            name: 'expirationDate',
                            value: formatedDate,
                          },
                        })
                      }
                    />
                  </div>
                </CCol>
              </CRow>
            </CCol>
            <CCol>
              <CCard>
                <CCardHeader>Profile notes</CCardHeader>
                <CCardBody>
                  <CommentList entityType="client" entityId={clientId} />
                  <CommentForm type="client" entityId={clientId} />
                </CCardBody>
              </CCard>
              <Divider />
              {clientId != '0' && <HistoriesWidget clientId={clientId} />}
            </CCol>
            <div className="mt-2">
              <strong>Manager:</strong> {manager}
            </div>
          </CRow>
        </CCardBody>
      </CCard>
      <Guard permission="view_uploaded_image_verification">
        <CRow>
          <CCol xs={12}>
            <h3>Uploaded Documents</h3>
            {browserFileNames?.map((link, index) => {
              return (
                <div className="d-flex flex-column">
                  <div key={link}>
                    <Image width={400} src={link} />
                  </div>
                  <a key={link} href={link} target="_blank">
                    doc {index}
                  </a>
                </div>
              )
            })}
          </CCol>
        </CRow>
      </Guard>
    </CCol>
  )
}

export const CardVerificationRequestEditDetail = () => {
  const cardVerificationRequest = useCardVerificationRequest()
  const { requestId, status } = cardVerificationRequest
  return (
    <div>
      <CRow>
        <CButtonGroup
          role="group"
          aria-label="Basic example"
          className="gap-5 align-items-baseline justify-content-center"
        >
          <CButton
            color="primary"
            className="col-2"
            onClick={() =>
              submitEditCardVerificationRequest(cardVerificationRequest)
            }
          >
            Save
          </CButton>
          <CButton
            color="primary"
            className="col-2"
            onClick={() =>
              submitEditCardVerificationRequest(cardVerificationRequest)
            }
          >
            Update expiration
          </CButton>
          <CButton
            color="success"
            className="col-2"
            onClick={() => {
              submitApprove(cardVerificationRequest)
            }}
          >
            Approve
          </CButton>
          <RejectModal
            color="danger"
            className="col-2"
            requestId={requestId}
            status={status}
          />
          <CButton color="warning" className="col-2">
            Send to senior manager
          </CButton>
          <CButton
            color="secondary"
            className="col-2"
            href={path.cardVerifications.entry()}
          >
            Exit
          </CButton>
        </CButtonGroup>
        <Divider />
        <CCard className="mb-4">
          <CCardHeader className="d-sm-flex justify-content-between align-items-center">
            <strong>Client card</strong>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol>
                <strong>User ID:</strong>{' '}
                <a
                  target="_blank"
                  href={path.clients.details(
                    cardVerificationRequest.client.clientId,
                  )}
                >
                  {' '}
                  {cardVerificationRequest.client.clientId}
                </a>
              </CCol>
              <CCol>
                <strong>Full name:</strong>{' '}
                {cardVerificationRequest.client.fullName}
              </CCol>
              <CCol>
                <strong>E-mail:</strong> {cardVerificationRequest.client.email}
              </CCol>
              <CCol className="d-flex align-items-center gap-2">
                <strong>Is verified:</strong>
                {cardVerificationRequest.client.verified ? (
                  <CheckCircleOutlined className={'text-success'} />
                ) : (
                  <CloseSquareOutlined className={'text-danger'} />
                )}
              </CCol>
              <CCol className="d-flex align-items-center gap-2">
                <strong>Is partner:</strong>
                {cardVerificationRequest.client.partner ? (
                  <CheckCircleOutlined className={'text-success'} />
                ) : (
                  <CloseSquareOutlined className={'text-danger'} />
                )}
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
        <CardVerificationRequestEdit
          cardVerificationRequest={cardVerificationRequest}
        ></CardVerificationRequestEdit>
      </CRow>
    </div>
  )
}
